import { GwSummary, SummaryEvent, SummaryEventType } from "../api/leagues";
import { ArchiveStandingRow, groupStandingsByRank } from "./trophies";

/**
 * Mirrors backend/src/lib/gwSummary.ts's generateSeasonSummaryEvents, ported to run client-side
 * against a frozen archive's standings instead of a live league — there is no backend route to
 * call this from, since the source League/LeagueMember data behind an archive is permanently
 * deleted and the standings payload already has everything this needs.
 *
 * Uses groupStandingsByRank (trophies.ts) for the same placement-by-group-order reasoning
 * archiveTrophies uses — so there is no TARGET-only SEASON_TARGET_BUSTS case, since no archived
 * mode ever used TARGET, and no prioritiseAndDiversify/groupEvents trimming pass, since this can
 * never produce more than 4 candidate events (well under any character budget).
 */
export function computeArchiveSeasonSummary(standings: ArchiveStandingRow[]): {
  gwSummary: GwSummary;
  members: { id: string; displayName: string }[];
} {
  const members = standings.map((s) => ({ id: s.userId ?? `guest-${s.rank}`, displayName: s.displayName }));
  const groups = groupStandingsByRank(standings);

  const placeEvent = (type: SummaryEventType, group: ArchiveStandingRow[]): SummaryEvent => {
    const memberIds = group.map((s) => s.userId ?? `guest-${s.rank}`);
    const score = group[0].score;
    return memberIds.length === 1
      ? { type, memberIds, params: { memberId: memberIds[0], score } }
      : { type, memberIds, params: { memberIds: memberIds.join(","), count: memberIds.length, score } };
  };

  const events: SummaryEvent[] = [];
  if (groups[0]) events.push(placeEvent("SEASON_WINNER", groups[0]));
  if (groups[1]) events.push(placeEvent("SEASON_RUNNER_UP", groups[1]));
  if (groups[2]) events.push(placeEvent("SEASON_THIRD", groups[2]));
  // Only call out last place if it's a distinct group beyond the top 3 already named.
  if (groups.length >= 4) events.push(placeEvent("SEASON_LAST", groups[groups.length - 1]));

  return {
    gwSummary: { gameweekNumber: 0, gameweekName: "Final", gameweekShortName: "FIN", isSeasonSummary: true, events },
    members,
  };
}
